import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Lock, User, Mail, Phone, Loader2, Eye, EyeOff } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterpriseLogin = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });

  const [registerData, setRegisterData] = useState({
    company_name: "",
    company_email: "",
    company_phone: "",
    admin_name: "",
    admin_email: "",
    admin_password: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/enterprise/login`, loginData);
      
      if (response.data.success) {
        localStorage.setItem('enterprise_token', response.data.token);
        localStorage.setItem('enterprise_user', JSON.stringify(response.data.user));
        localStorage.setItem('enterprise_company', JSON.stringify(response.data.company));
        toast.success("Koneksyon reyisi!");
        navigate("/enterprise/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan koneksyon");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/enterprise/register`, {
        company: {
          name: registerData.company_name,
          email: registerData.company_email,
          phone: registerData.company_phone
        },
        admin_user: {
          email: registerData.admin_email,
          password: registerData.admin_password,
          name: registerData.admin_name
        }
      });
      
      if (response.data.success) {
        localStorage.setItem('enterprise_token', response.data.token);
        localStorage.setItem('enterprise_user', JSON.stringify(response.data.user));
        localStorage.setItem('enterprise_company', JSON.stringify(response.data.company));
        toast.success("Enskrisyon reyisi!");
        navigate("/enterprise/dashboard");
      }
    } catch (error) {
      console.error("Register error:", error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan enskrisyon");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#232F3E] to-[#131921] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl bg-[#FF9900] flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-12 h-12 text-black" />
          </div>
          <h1 className="text-2xl font-bold text-white">
            BVGLINKAMAZON <span className="text-[#FF9900]">Business</span>
          </h1>
          <p className="text-gray-400 mt-2">Platfòm Procurement pou Antrepriz</p>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 bg-white/10 rounded-lg p-1">
          <button
            onClick={() => setIsLogin(true)}
            className={`flex-1 py-2 rounded-md font-medium transition-colors ${
              isLogin ? 'bg-[#FF9900] text-black' : 'text-gray-300 hover:text-white'
            }`}
          >
            Konekte
          </button>
          <button
            onClick={() => setIsLogin(false)}
            className={`flex-1 py-2 rounded-md font-medium transition-colors ${
              !isLogin ? 'bg-[#FF9900] text-black' : 'text-gray-300 hover:text-white'
            }`}
          >
            Enskri
          </button>
        </div>

        {/* Forms */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {isLogin ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label>Imèl</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="ou@antrepriz.ht"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Modpas</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="pl-10 pr-10 h-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold py-6 text-lg"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                Konekte
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="border-b pb-4 mb-4">
                <h3 className="font-semibold text-gray-700 mb-3">Enfòmasyon Antrepriz</h3>
                
                <div className="space-y-3">
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Non Antrepriz"
                      value={registerData.company_name}
                      onChange={(e) => setRegisterData({...registerData, company_name: e.target.value})}
                      className="pl-10"
                      required
                    />
                  </div>
                  
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="Imèl Antrepriz"
                      value={registerData.company_email}
                      onChange={(e) => setRegisterData({...registerData, company_email: e.target.value})}
                      className="pl-10"
                      required
                    />
                  </div>
                  
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Telefòn"
                      value={registerData.company_phone}
                      onChange={(e) => setRegisterData({...registerData, company_phone: e.target.value})}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Kont Administratè</h3>
                
                <div className="space-y-3">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Non Konplè"
                      value={registerData.admin_name}
                      onChange={(e) => setRegisterData({...registerData, admin_name: e.target.value})}
                      className="pl-10"
                      required
                    />
                  </div>
                  
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="Imèl Admin"
                      value={registerData.admin_email}
                      onChange={(e) => setRegisterData({...registerData, admin_email: e.target.value})}
                      className="pl-10"
                      required
                    />
                  </div>
                  
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="password"
                      placeholder="Modpas (min 6 karaktè)"
                      value={registerData.admin_password}
                      onChange={(e) => setRegisterData({...registerData, admin_password: e.target.value})}
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold py-6 text-lg mt-4"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                Kreye Kont
              </Button>
            </form>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6 space-y-2">
          <Link to="/" className="text-gray-400 hover:text-[#FF9900] transition-colors text-sm block">
            ← Retounen nan sit la
          </Link>
          <p className="text-gray-500 text-xs">
            Pou acha pèsonèl, itilize <Link to="/" className="text-[#FF9900]">boutik kat kado</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

